import { Stack } from '@mui/material';
import { PutApiV5FilesIdRename200File } from '@liscio/api';
import {
  getGetApiV5FilesPendingReviewQueryKey,
  usePutApiV5FilesIdRename,
} from '@liscio/api';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom-latest';
import { AlertMessage, isUserEmployee } from 'utilities/utils';
import { useTrackEvent } from 'analytics/useTrackEvent';
import { useGetHome } from 'hooks/sms-hooks';
import {
  AIFileNameTitle,
  AIFileNameInfo,
  AIFileNameForm,
  AINamingFormatSettings,
} from './components';

export interface AIFileNameManagementProps {
  fileUuid: string;
  fileName?: string;
  fileSize?: number;
  uploadedBy?: string;
  originalName?: string;
  onChangeFileName: (newFile: PutApiV5FilesIdRename200File) => void;
  aiIngestionStatus?: string;
}

export const AIFileNameManagement = ({
  fileName,
  fileUuid,
  fileSize,
  uploadedBy,
  originalName,
  onChangeFileName,
  aiIngestionStatus,
}: AIFileNameManagementProps) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const trackEvent = useTrackEvent();
  const { data: homeData } = useGetHome();

  const { mutateAsync: renameFile, isLoading: isRenamingFile } =
    usePutApiV5FilesIdRename();

  const handleChangeName = async (
    newName: string,
    aiFileNameSuggestionUuid?: string | null,
  ) => {
    const oldFileNameExtension = fileName?.split('.').pop();
    const newFileName = oldFileNameExtension
      ? `${newName}.${oldFileNameExtension}`
      : newName;

    if (newFileName && fileUuid) {
      try {
        // Update filename
        const response = await renameFile({
          id: fileUuid,
          data: {
            name: newFileName,
            ai_file_name_suggestion_uuid: aiFileNameSuggestionUuid ?? null,
          },
        });

        // Update local state and notify parent
        onChangeFileName(response.file);

        // Track onboarding analytics if coming from onboarding flow and user is an employee
        const comingFrom = searchParams.get('comingFrom');

        if (comingFrom === 'onboarding' && isUserEmployee()) {
          trackEvent('file_renamed', {
            source: 'onboarding',
            user_role: 'firm_employee',
            company_name: homeData?.data?.userpilot_data?.firm_name,
            onboarding_step: 'file_renamed',
            naming_method: 'ai_legacy',
          });
        }
      } catch (error) {
        AlertMessage('error', 'Failed to rename file', 3000);
      }

      // Invalidate the pending review query to refresh the file list
      queryClient.removeQueries({
        queryKey: getGetApiV5FilesPendingReviewQueryKey(),
      });
    }
  };

  return (
    <Stack
      sx={{ marginBottom: '8px' }}
      className="ai-filename-management"
      data-tour="ai-filename-management"
    >
      <AIFileNameTitle />
      <AIFileNameInfo
        fileName={fileName}
        fileSize={fileSize}
        uploadedBy={uploadedBy}
      />
      <AIFileNameForm
        fileUuid={fileUuid}
        onSubmit={(data) => {
          handleChangeName(
            data.newFileName,
            data.ai_file_name_suggestion_uuid ?? null,
          );
        }}
        isLoading={isRenamingFile}
        fileName={fileName}
        originalName={originalName}
        aiIngestionStatus={aiIngestionStatus}
      />
      <AINamingFormatSettings />
    </Stack>
  );
};
